import { useParams } from "react-router-dom";
import { Category } from "./Categories";

// Τα hooks των ασθενών
import { usePatients } from "../../hooks/usePatients";
import { usePatientAllergies } from "../../hooks/usePatientAllergies";
import { usePatientClinicalScore } from "../../hooks/usePatientClinicalScore";
import { usePatientDocuments } from "../../hooks/usePatientDocuments";
import { usePatientImplants } from "../../hooks/usePatientImplants";
import { usePatientRecords } from "../../hooks/usePatientRecords";
import { usePatientRehabProtocols } from "../../hooks/usePatientRehabProtocols";
import { usePatientMedicines } from "../../hooks/usePatientMedicines";
import { usePatientSurgeries } from "../../hooks/usePatientSurgeries";

// Τα γενικά hooks για τα options των Selects
import { useAllergies } from "../../hooks/useAllergies";
import { useImplants } from "../../hooks/useImplants";
import { useMedicines } from "../../hooks/useMedicines";
import { useSurgeryTypes } from "../../hooks/useSurgeryTypes";
import { useDocumentTypes } from "../../hooks/useDocumentTypes";
import { useRecordCategories } from "../../hooks/useRecordCategories";
import { useRehabFacilities } from "../../hooks/useRahabFacilties";

export const useEntityAdapter = (category: Category) => {
  const { patientId } = useParams<{ patientId: string }>();

  // 1. Data hooks για τον ασθενέα
  const patients = usePatients(patientId);
  const patientAllergies = usePatientAllergies(patientId);
  const patientClinicalScores = usePatientClinicalScore(patientId);
  const patientDocuments = usePatientDocuments(patientId);
  const patientImplants = usePatientImplants(patientId);
  const patientRecords = usePatientRecords(patientId);
  const patientRehabProtocols = usePatientRehabProtocols(patientId);
  const patientMedicines = usePatientMedicines(patientId);
  const patientSurgeries = usePatientSurgeries(patientId);

  // 2. Global hooks για τα dropdowns/options
  const { allergies: globalAllergies = [] } = useAllergies();
  const { implants: globalImplants = [] } = useImplants();
  const { medicines: globalMedicines = [] } = useMedicines();
  const { surgeryTypes: globalSurgeryTypes = [] } = useSurgeryTypes();
  const { documentTypes: globalDocumentTypes = [] } = useDocumentTypes();
  const { recordCategories: globalRecordCategories = [] } =
    useRecordCategories();
  const { rehabFacilities: globalRehabFacilities = [] } = useRehabFacilities();

  switch (category) {
    case "patient": {
      return {
        data: patients.patient ?? null,
        isLoading: patients.isLoading,
        modalState: patients.modalState,
        update: patients.updatePatient,
        closeModal: patients.closeModal,
      };
    }

    case "patientAllergy": {
      const options =
        globalAllergies?.map((item: any) => ({
          value: item.allergyId ?? item.AllergyId ?? item.id,
          label:
            item.allergyDescr ??
            item.AllergyDescr ??
            item.idDescr ??
            item.name ??
            "Χωρίς περιγραφή",
        })) ?? [];

      return {
        data: patientAllergies.patientAllergies,
        isLoading: patientAllergies.isLoading,
        create: patientAllergies.createPatientAllergy,
        update: patientAllergies.updatePatientAllergy,
        remove: patientAllergies.deletePatientAllergies,
        modalState: patientAllergies.modalState,
        closeModal: patientAllergies.closeModal,
        options,
      };
    }

    case "patientImplant": {
      const options =
        globalImplants?.map((item: any) => ({
          value: item.implantId ?? item.ImplantId ?? item.id,
          label:
            item.idDescr ??
            item.implantDescr ??
            item.modelName ??
            item.ModelName ??
            item.manufacturer ??
            "Χωρίς περιγραφή",
        })) ?? [];

      return {
        data: patientImplants.patientImplants,
        isLoading: patientImplants.isLoading,
        create: patientImplants.createPatientImplant,
        update: patientImplants.updatePatientImplant,
        remove: patientImplants.deletePatientImplants,
        modalState: patientImplants.modalState,
        closeModal: patientImplants.closeModal,
        options,
      };
    }

    case "patientMedicine": {
      const options =
        globalMedicines?.map((item: any) => ({
          value: item.medicineId ?? item.MedicineId ?? item.id,
          label:
            item.name ??
            item.Name ??
            item.medicineDescr ??
            item.MedicineDescr ??
            item.idDescr ??
            "Χωρίς περιγραφή",
        })) ?? [];

      return {
        data: patientMedicines.patientMedicines,
        isLoading: patientMedicines.isLoading,
        create: patientMedicines.createPatientMedicine,
        update: patientMedicines.updatePatientMedicine,
        remove: patientMedicines.deletePatientMedicines,
        modalState: patientMedicines.modalState,
        closeModal: patientMedicines.closeModal,
        options,
      };
    }

    case "patientSurgery": {
      const options =
        globalSurgeryTypes?.map((item: any) => ({
          value: item.surgeryTypeId ?? item.SurgeryTypeId ?? item.id,
          label:
            item.surgeryTypeDescr ??
            item.SurgeryTypeDescr ??
            item.idDescr ??
            item.description ??
            "Χωρίς περιγραφή",
        })) ?? [];

      return {
        data: patientSurgeries.patientSurgeries,
        isLoading: patientSurgeries.isLoading,
        create: patientSurgeries.createPatientSurgery,
        update: patientSurgeries.updatePatientSurgery,
        remove: patientSurgeries.deletePatientSurgeries,
        modalState: patientSurgeries.modalState,
        closeModal: patientSurgeries.closeModal,
        options,
      };
    }

    case "patientDocument": {
      const options =
        globalDocumentTypes?.map((item: any) => ({
          value: item.documentTypeId ?? item.DocumentTypeId ?? item.id,
          label:
            item.documentDescr ??
            item.DocumentDescr ??
            item.idDescr ??
            "Χωρίς περιγραφή",
        })) ?? [];

      return {
        data: patientDocuments.patientDocuments,
        isLoading: patientDocuments.isLoading,
        create: patientDocuments.createPatientDocument,
        update: patientDocuments.updatePatientDocument,
        remove: patientDocuments.deletePatientDocuments,
        modalState: patientDocuments.modalState,
        closeModal: patientDocuments.closeModal,
        options,
      };
    }

    case "patientRecord": {
      const options =
        globalRecordCategories?.map((item: any) => ({
          value:
            item.recordId ?? item.RecordId ?? item.recordCategoryId ?? item.id,
          label:
            item.categoryName ??
            item.CategoryName ??
            item.recordCategoryDescr ??
            item.idDescr ??
            "Χωρίς περιγραφή",
        })) ?? [];

      return {
        data: patientRecords.patientRecords,
        isLoading: patientRecords.isLoading,
        create: patientRecords.createPatientRecord,
        update: patientRecords.updatePatientRecord,
        remove: patientRecords.deletePatientRecords,
        modalState: patientRecords.modalState,
        closeModal: patientRecords.closeModal,
        options,
      };
    }

    case "patientRehabProtocol": {
      const options =
        globalRehabFacilities?.map((item: any) => ({
          value:
            item.rehabId ??
            item.RehabId ??
            item.facilityId ??
            item.FacilityId ??
            item.id,
          label:
            item.facilityDescr ??
            item.FacilityDescr ??
            item.protocolType ??
            item.idDescr ??
            "Χωρίς περιγραφή",
        })) ?? [];

      return {
        data: patientRehabProtocols.patientRehabProtocols,
        isLoading: patientRehabProtocols.isLoading,
        create: patientRehabProtocols.createPatientRehabProtocol,
        update: patientRehabProtocols.updatePatientRehabProtocol,
        remove: patientRehabProtocols.deletePatientRehabProtocols,
        modalState: patientRehabProtocols.modalState,
        closeModal: patientRehabProtocols.closeModal,
        options,
      };
    }

    case "patientClinicalScore":
      return {
        data: patientClinicalScores.patientClinicalScores,
        isLoading: patientClinicalScores.isLoading,
        create: patientClinicalScores.createPatientClinicalScore,
        update: patientClinicalScores.updatePatientClinicalScore,
        remove: patientClinicalScores.deletePatientClinicalScores,
        modalState: patientClinicalScores.modalState,
        closeModal: patientClinicalScores.closeModal,
      };

    default:
      return {
        data: [],
        isLoading: false,
        create: async () => {},
        update: async () => {},
        remove: async () => {},
        modalState: { isOpen: false, title: "", message: "", type: "success" },
        closeModal: () => {},
      };
  }
};
